
import React from 'react';

interface Filters {
  role: string[];
  location: string[];
  status: string[];
}

interface JobFiltersProps {
  filters: Filters;
  onFilterChange: (filterName: keyof Filters, value: string) => void;
  roles: string[];
  locations: string[];
}

const formatLabel = (s: string) => {
  if (!s) return '';
  const text = s.replace(/-/g, ' ');
  return text.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const FilterChip: React.FC<{
  label: string;
  isSelected: boolean;
  onClick: () => void;
}> = ({ label, isSelected, onClick }) => {
  const baseClasses = "min-h-10 px-4 py-2 text-sm font-bold rounded-full cursor-pointer transition-colors duration-200 border focus:outline-none focus:ring-2 focus:ring-[#A1AAD9]";
  const selectedClasses = "bg-[#5A46AA] text-white border-[#5A46AA] shadow-sm";
  const unselectedClasses = "bg-[#FFFAED] text-[#636363] border-[#A1AAD9]/55 hover:bg-[#FFF5D7] hover:border-[#5A46AA]";
  
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`${baseClasses} ${isSelected ? selectedClasses : unselectedClasses}`}
      aria-pressed={isSelected}
    >
      {label}
    </button>
  );
};

const FilterSection: React.FC<{
  title: string;
  options: string[];
  selectedOptions: string[];
  onToggle: (option: string) => void;
  shouldFormat?: boolean;
}> = ({ title, options, selectedOptions, onToggle, shouldFormat = false }) => (
  <div className="grid min-w-0 gap-3 py-3 first:pt-0 last:pb-0 sm:grid-cols-[112px_minmax(0,1fr)] sm:items-start sm:gap-4">
    <div className="flex min-h-10 items-center gap-2 sm:items-start sm:pt-2.5"><h4 className="text-xs font-extrabold uppercase tracking-[0.06em] text-[#342F43]">{title}</h4>{selectedOptions.length > 0 && <span className="inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[#5A46AA] px-1.5 text-[10px] font-bold text-white">{selectedOptions.length}</span>}</div>
    <div className="flex min-w-0 flex-wrap gap-2">
      {options.map(option => (
        <FilterChip
          key={option}
          label={shouldFormat ? formatLabel(option) : option}
          isSelected={selectedOptions.includes(option)}
          onClick={() => onToggle(option)}
        />
      ))}
    </div>
  </div>
);

const JobFilters: React.FC<JobFiltersProps> = ({ filters, onFilterChange, roles, locations }) => {
  return (
    <div className="divide-y divide-[#A1AAD9]/30">
      <FilterSection
        title="Role"
        options={roles}
        selectedOptions={filters.role}
        onToggle={(option) => onFilterChange('role', option)}
        shouldFormat
      />
      <FilterSection
        title="Location"
        options={locations}
        selectedOptions={filters.location}
        onToggle={(option) => onFilterChange('location', option)}
      />
    </div>
  );
};

export default JobFilters;
